import type { BranchRecord, ClientRecord } from '@occ/contracts';
import { useCan } from '@refinedev/core';
import type { ColumnDef } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { describeHttpError } from '../../api/http';
import { ErrorNotice } from '../../components/ErrorNotice';
import {
  Alert,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  DataTableColumnHeader,
  EmptyState,
  Field,
  Loading,
  Modal,
  Notice,
  Toasts,
  useToasts,
} from '../../components/ui';
import {
  createBranch,
  createClient,
  deleteBranch,
  deleteClient,
  listBranches,
  listClients,
  updateBranch,
  updateClient,
} from '../../data/admin';
import { useHierarchy } from '../../hierarchy/HierarchyProvider';

/**
 * CRUD de la jerarquía Cliente→Sucursal→Nodo (RF-07).
 *
 * Los nodos no se crean ni se borran aquí: **aparecen al enrolarse** y se editan
 * desde su propia ficha, que es donde el técnico los está mirando. Borrar la fila
 * de un nodo dejaría su bitácora sin sujeto, y lo que se hace con una máquina que
 * sale del parque es desinstalar el agente.
 *
 * El borrado de un cliente o de una sucursal pide teclear el nombre. No es
 * ceremonia: se lleva por delante todo lo que cuelga, y estas dos son las únicas
 * operaciones de la administración que no se pueden deshacer desde el propio
 * panel.
 */

type ClientForm = { id: string | null; name: string; notes: string };
type BranchForm = { id: string | null; clientId: string; name: string; address: string };

export function HierarchyAdminPage() {
  const { refresh } = useHierarchy();
  const { data: canEditClients } = useCan({ resource: 'clients', action: 'edit' });
  const { data: canEditBranches } = useCan({ resource: 'branches', action: 'edit' });

  const [clients, setClients] = useState<ClientRecord[]>([]);
  const [branches, setBranches] = useState<BranchRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const [clientForm, setClientForm] = useState<ClientForm | null>(null);
  const [branchForm, setBranchForm] = useState<BranchForm | null>(null);
  const [deletingClient, setDeletingClient] = useState<ClientRecord | null>(null);
  const [deletingBranch, setDeletingBranch] = useState<BranchRecord | null>(null);
  const [busy, setBusy] = useState(false);

  const toasts = useToasts();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Las dos listas en paralelo: son independientes y encadenarlas doblaría la
      // espera sin ganar nada.
      const [nextClients, nextBranches] = await Promise.all([listClients(), listBranches()]);
      setClients(nextClients);
      setBranches(nextBranches);
      setError(null);
    } catch (cause) {
      setError(cause);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const after = async (message: string) => {
    toasts.ok(message);
    await load();
    // El árbol lateral y el índice para el RBAC viven en el proveedor: sin esto
    // una sucursal nueva no aparecería hasta recargar la página.
    refresh();
  };

  const submitClient = async () => {
    if (!clientForm) return;
    setBusy(true);
    try {
      if (clientForm.id) {
        await updateClient(clientForm.id, { name: clientForm.name, notes: clientForm.notes });
      } else {
        await createClient(clientForm.name, clientForm.notes);
      }
      setClientForm(null);
      await after(clientForm.id ? 'Cliente actualizado.' : 'Cliente creado.');
    } catch (cause) {
      toasts.fail(describeHttpError(cause));
    } finally {
      setBusy(false);
    }
  };

  const submitBranch = async () => {
    if (!branchForm) return;
    setBusy(true);
    try {
      if (branchForm.id) {
        await updateBranch(branchForm.id, { name: branchForm.name, address: branchForm.address });
      } else {
        await createBranch(branchForm.clientId, branchForm.name, branchForm.address);
      }
      setBranchForm(null);
      await after(branchForm.id ? 'Sucursal actualizada.' : 'Sucursal creada.');
    } catch (cause) {
      toasts.fail(describeHttpError(cause));
    } finally {
      setBusy(false);
    }
  };

  const removeClient = async () => {
    if (!deletingClient) return;
    setBusy(true);
    try {
      await deleteClient(deletingClient.id);
      setDeletingClient(null);
      await after('Cliente borrado.');
    } catch (cause) {
      toasts.fail(describeHttpError(cause));
    } finally {
      setBusy(false);
    }
  };

  const removeBranch = async () => {
    if (!deletingBranch) return;
    setBusy(true);
    try {
      await deleteBranch(deletingBranch.id);
      setDeletingBranch(null);
      await after('Sucursal borrada.');
    } catch (cause) {
      toasts.fail(describeHttpError(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="grid gap-4">
      <Toasts toasts={toasts.toasts} />

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Jerarquía</h1>
          <p className="text-sm opacity-60">
            Clientes y sucursales. Los nodos aparecen al enrolarse y se editan en su ficha.
          </p>
        </div>
        <Button
          variant="primary"
          disabled={!canEditClients?.can}
          title={canEditClients?.can ? undefined : canEditClients?.reason}
          onClick={() => setClientForm({ id: null, name: '', notes: '' })}
        >
          Nuevo cliente
        </Button>
      </div>

      {!canEditClients?.can && (
        <Alert>
          {canEditClients?.reason ?? 'Solo un administrador del tenant edita la jerarquía.'}
        </Alert>
      )}

      {error !== null && <ErrorNotice error={error} what="Cargar la jerarquía" />}

      {loading && clients.length === 0 ? (
        <Loading label="Cargando clientes…" />
      ) : clients.length === 0 ? (
        <Card>
          <EmptyState title="Ningún cliente todavía">
            Un cliente agrupa sucursales, y una sucursal agrupa nodos. Crea el primero para poder
            emitir tokens de instalación.
          </EmptyState>
        </Card>
      ) : (
        clients.map((client) => (
          <Card
            key={client.id}
            title={
              <span className="inline-flex items-center gap-2">
                {client.name}
                <Badge>{client.nodeCount ?? 0} nodos</Badge>
              </span>
            }
            actions={
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  disabled={!canEditBranches?.can}
                  title={canEditBranches?.can ? undefined : canEditBranches?.reason}
                  onClick={() =>
                    setBranchForm({ id: null, clientId: client.id, name: '', address: '' })
                  }
                >
                  Nueva sucursal
                </Button>
                <Button
                  variant="ghost"
                  disabled={!canEditClients?.can}
                  onClick={() =>
                    setClientForm({ id: client.id, name: client.name, notes: client.notes ?? '' })
                  }
                >
                  Editar
                </Button>
                <Button
                  variant="danger"
                  disabled={!canEditClients?.can}
                  onClick={() => setDeletingClient(client)}
                >
                  Borrar
                </Button>
              </div>
            }
          >
            <div className="grid gap-3">
              {client.notes && <p className="text-sm opacity-70">{client.notes}</p>}

              <BranchTable
                branches={branches.filter((branch) => branch.clientId === client.id)}
                canEdit={canEditBranches?.can === true}
                editReason={canEditBranches?.reason}
                onEdit={(branch) =>
                  setBranchForm({
                    id: branch.id,
                    clientId: branch.clientId,
                    name: branch.name,
                    address: branch.address ?? '',
                  })
                }
                onDelete={setDeletingBranch}
              />
            </div>
          </Card>
        ))
      )}

      {/* ── Formularios ── */}
      <Modal
        open={clientForm !== null}
        title={clientForm?.id ? 'Editar cliente' : 'Nuevo cliente'}
        onClose={() => setClientForm(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setClientForm(null)} disabled={busy}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              disabled={busy || (clientForm?.name.trim() ?? '') === ''}
              onClick={() => void submitClient()}
            >
              {busy ? 'Guardando…' : 'Guardar'}
            </Button>
          </>
        }
      >
        {clientForm && (
          <div className="grid gap-3">
            <Field
              label="Nombre"
              value={clientForm.name}
              autoFocus
              onChange={(event) => setClientForm({ ...clientForm, name: event.target.value })}
            />
            <Field
              label="Notas (opcional)"
              value={clientForm.notes}
              hint="Contexto para quien coja el incidente: horarios, contacto, particularidades."
              onChange={(event) => setClientForm({ ...clientForm, notes: event.target.value })}
            />
          </div>
        )}
      </Modal>

      <Modal
        open={branchForm !== null}
        title={branchForm?.id ? 'Editar sucursal' : 'Nueva sucursal'}
        onClose={() => setBranchForm(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setBranchForm(null)} disabled={busy}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              disabled={busy || (branchForm?.name.trim() ?? '') === ''}
              onClick={() => void submitBranch()}
            >
              {busy ? 'Guardando…' : 'Guardar'}
            </Button>
          </>
        }
      >
        {branchForm && (
          <div className="grid gap-3">
            <Field
              label="Nombre"
              value={branchForm.name}
              autoFocus
              onChange={(event) => setBranchForm({ ...branchForm, name: event.target.value })}
            />
            <Field
              label="Dirección (opcional)"
              value={branchForm.address}
              onChange={(event) => setBranchForm({ ...branchForm, address: event.target.value })}
            />
          </div>
        )}
      </Modal>

      {/* ── Borrados ── */}
      <ConfirmDialog
        open={deletingClient !== null}
        title="Borrar cliente"
        confirmLabel="Borrar"
        confirmText={deletingClient?.name}
        busy={busy}
        onCancel={() => setDeletingClient(null)}
        onConfirm={() => void removeClient()}
      >
        <p>
          <strong>{deletingClient?.name}</strong> tiene {deletingClient?.branchCount ?? 0}{' '}
          sucursales y {deletingClient?.nodeCount ?? 0} nodos.
        </p>
        <Notice tone="warn">
          Los nodos enrolados dejarán de tener a quién pertenecer. Si lo que quieres es dejar de dar
          soporte a un cliente, quita los permisos de los técnicos: eso sí se deshace.
        </Notice>
      </ConfirmDialog>

      <ConfirmDialog
        open={deletingBranch !== null}
        title="Borrar sucursal"
        confirmLabel="Borrar"
        confirmText={deletingBranch?.name}
        busy={busy}
        onCancel={() => setDeletingBranch(null)}
        onConfirm={() => void removeBranch()}
      >
        <p>
          <strong>{deletingBranch?.name}</strong> tiene {deletingBranch?.nodeCount ?? 0} nodos.
        </p>
        <Notice tone="warn">
          Un alias de nodo es único <em>por sucursal</em>: mover los nodos a otra antes de borrar
          puede provocar colisiones de alias.
        </Notice>
      </ConfirmDialog>
    </section>
  );
}

function BranchTable({
  branches,
  canEdit,
  editReason,
  onEdit,
  onDelete,
}: {
  branches: BranchRecord[];
  canEdit: boolean;
  editReason?: string;
  onEdit: (branch: BranchRecord) => void;
  onDelete: (branch: BranchRecord) => void;
}) {
  const columns = useMemo<ColumnDef<BranchRecord>[]>(
    () => [
      {
        id: 'sucursal',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Sucursal" />,
        accessorFn: (branch) => branch.name,
        meta: { label: 'Sucursal' },
      },
      {
        id: 'direccion',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Dirección" />,
        accessorFn: (branch) => branch.address ?? '—',
        meta: { label: 'Dirección' },
        cell: ({ getValue }) => <span className="opacity-70">{getValue<string>()}</span>,
      },
      {
        id: 'nodos',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Nodos" />,
        accessorFn: (branch) => branch.onlineNodeCount ?? 0,
        meta: { label: 'Nodos' },
        cell: ({ row }) => (
          <Link
            to={`/?branchId=${row.original.id}`}
            className="opacity-70 hover:underline hover:opacity-100"
          >
            {row.original.onlineNodeCount ?? 0} / {row.original.nodeCount ?? 0} en línea
          </Link>
        ),
      },
      {
        id: 'acciones',
        header: 'Acciones',
        meta: { label: 'Acciones' },
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              disabled={!canEdit}
              title={canEdit ? undefined : editReason}
              onClick={() => onEdit(row.original)}
            >
              Editar
            </Button>
            <Button variant="danger" disabled={!canEdit} onClick={() => onDelete(row.original)}>
              Borrar
            </Button>
          </div>
        ),
      },
    ],
    [canEdit, editReason, onEdit, onDelete],
  );

  if (branches.length === 0) {
    return (
      <p className="text-sm opacity-60">
        Sin sucursales. Hace falta al menos una para emitir un token de instalación.
      </p>
    );
  }

  return <DataTable columns={columns} data={branches} pageSize={0} />;
}
